import React from "react";
import type { NotificationEntity } from "@/types/notifications";
import { NotificationRow } from "./NotificationRow";
import { Loader2 } from "lucide-react";

export function NotificationsList(props: {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  items: NotificationEntity[];
  emptyText: string;
  loadingText: string;
  onMarkRead: (id: string) => void;
  onLoadMore: () => void;
}) {
  const {
    scrollContainerRef,
    isLoading,
    isLoadingMore,
    hasMore,
    items,
    emptyText,
    loadingText,
    onMarkRead,
    onLoadMore,
  } = props;

  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const root = scrollContainerRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasMore) return;
        onLoadMore();
      },
      { root, rootMargin: "120px", threshold: 0.01 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [scrollContainerRef, hasMore, onLoadMore]);

  if (isLoading && items.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground">
        {loadingText}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground">
        {emptyText}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((n) => (
        <NotificationRow key={n.id} n={n} onMarkRead={onMarkRead} />
      ))}

      {/* Sentinel */}
      <div ref={sentinelRef} className="h-8" />

      {isLoadingMore ? (
        <div className="flex justify-center py-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : null}
    </div>
  );
}
